import { Component, ElementRef, Input, OnChanges, ViewChild, SimpleChanges, AfterViewInit, OnDestroy, HostListener, Inject, PLATFORM_ID, ViewEncapsulation, NgZone, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import * as d3 from 'd3';

export interface ParallelBarChartData {
    label: string;
    leftValue: number;
    rightValue: number;
    leftLabelFormatted?: string;
    rightLabelFormatted?: string;
}

@Component({
    selector: 'app-parallel-horizontal-bar-chart',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './parallel-horizontal-bar-chart.component.html',
    styleUrls: [],
    changeDetection: ChangeDetectionStrategy.Eager,
    encapsulation: ViewEncapsulation.None
})
export class ParallelHorizontalBarChartComponent implements OnChanges, AfterViewInit, OnDestroy {
    @Input() data: ParallelBarChartData[] = [];
    @Input() leftLabel: string = 'Left';
    @Input() rightLabel: string = 'Right';
    @Input() leftColor: string = '#3B82F6';
    @Input() rightColor: string = '#06B6D4';
    @Input() isDarkTheme: boolean = false;
    @Input() barHeight: number = 40;
    @Input() minHeight: number = 400;

    @ViewChild('chart', { static: true }) private chartContainer!: ElementRef;

    private isBrowser: boolean;
    /**
     * Watches the chart's own container so the SVG redraws whenever the
     * card it lives in changes width — e.g. the dashboard's Customize
     * Layout popup resizing the parent tile from `col-6` to `col-4`.
     * Window resize alone misses that case because the viewport itself
     * never changes.
     */
    private resizeObserver?: ResizeObserver;
    /** Coalesces a burst of size changes into a single re-render per frame. */
    private resizeRafId: number | null = null;
    /** Last container width we drew against; lets us skip no-op observer ticks. */
    private lastRenderedWidth = 0;

    constructor(
        @Inject(PLATFORM_ID) platformId: Object,
        private ngZone: NgZone,
    ) {
        this.isBrowser = isPlatformBrowser(platformId);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.isBrowser && (changes['data'] || changes['isDarkTheme'])) {
            // Small timeout to allow container resizing if needed
            setTimeout(() => this.createChart(), 0);
        }
    }

    ngAfterViewInit(): void {
        if (!this.isBrowser) return;
        setTimeout(() => this.createChart(), 0);
        this.observeContainerResize();
    }

    ngOnDestroy(): void {
        if (this.resizeObserver) {
            try { this.resizeObserver.disconnect(); } catch { /* noop */ }
            this.resizeObserver = undefined;
        }
        if (this.resizeRafId != null && typeof cancelAnimationFrame !== 'undefined') {
            cancelAnimationFrame(this.resizeRafId);
            this.resizeRafId = null;
        }
    }

    @HostListener('window:resize')
    onResize() {
        if (this.isBrowser) {
            this.scheduleRedraw();
        }
    }

    /**
     * Subscribes a `ResizeObserver` to the chart container so any width
     * change — viewport, parent flex/grid, or dashboard col-span — triggers
     * a redraw. Runs outside Angular's zone because we don't need change
     * detection for the d3 redraw, and the observer can fire frequently
     * during drag/resize gestures.
     */
    private observeContainerResize(): void {
        const el: HTMLElement | undefined = this.chartContainer?.nativeElement;
        if (!el || typeof ResizeObserver === 'undefined') return;
        this.ngZone.runOutsideAngular(() => {
            this.resizeObserver = new ResizeObserver(() => this.scheduleRedraw());
            this.resizeObserver.observe(el);
        });
    }

    /**
     * Debounces redraws to one per animation frame and skips frames where
     * the container width didn't actually change (e.g. height-only or
     * sub-pixel jitter), so we don't thrash the DOM during layout transitions.
     */
    private scheduleRedraw(): void {
        if (this.resizeRafId != null) return;
        this.resizeRafId = requestAnimationFrame(() => {
            this.resizeRafId = null;
            const el: HTMLElement | undefined = this.chartContainer?.nativeElement;
            if (!el) return;
            const width = el.offsetWidth;
            if (width === this.lastRenderedWidth) return;
            this.createChart();
        });
    }

    private createChart(): void {
        if (!this.data || this.data.length === 0) return;

        const element = this.chartContainer.nativeElement;
        d3.select(element).selectAll('*').remove();

        const margin = { top: 40, right: 20, bottom: 20, left: 20 };
        const containerWidth = element.offsetWidth;
        const width = containerWidth - margin.left - margin.right;
        const height = Math.max(this.minHeight, this.data.length * this.barHeight); // Auto height based on data

        if (width <= 0) return;
        this.lastRenderedWidth = containerWidth;

        const svg = d3.select(element)
            .append('svg')
            .attr('width', width + margin.left + margin.right)
            .attr('height', height + margin.top + margin.bottom)
            .append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        // Layout calculations
        const labelWidth = Math.min(150, width * 0.25); // Max 25% or 150px
        const chartWidth = width - labelWidth;
        const sideWidth = (chartWidth / 2) - 10; // Gap of 20px total (10px each side)
        const centerAxis = labelWidth + sideWidth + 10;

        // Scales
        const y = d3.scaleBand()
            .range([0, height])
            .domain(this.data.map(d => d.label))
            .padding(0.03);

        const maxLeft = d3.max(this.data, d => Number(d.leftValue)) || 0;
        const maxRight = d3.max(this.data, d => Number(d.rightValue)) || 0;

        // Independent scales for "full bar" look
        const xLeft = d3.scaleLinear()
            .domain([0, maxLeft])
            .range([0, sideWidth]);

        const xRight = d3.scaleLinear()
            .domain([0, maxRight])
            .range([0, sideWidth]);

        // Headers
        const textColor = this.isDarkTheme ? '#FFFFFF' : '#141437';
        const subTextColor = this.isDarkTheme ? '#A1A1AA' : '#7D7D93';

        // Header Group
        const headerGroup = svg.append('g').attr('transform', `translate(0, -15)`);

        // Country Header
        headerGroup.append('text')
            .attr('x', 0)
            .attr('y', 0)
            .text('Countries')
            .style('fill', subTextColor)
            .style('font-size', '14px')
            .style('font-weight', '500');

        // Left Metric Header
        headerGroup.append('text')
            .attr('x', centerAxis - 10) // Align to right side of left section (near center)
            .attr('y', 0)
            .text(this.leftLabel)
            .style('fill', subTextColor)
            .style('font-size', '14px')
            .style('font-weight', '500')
            .style('text-anchor', 'end'); // Right align text

        // Right Metric Header
        headerGroup.append('text')
            .attr('x', centerAxis + 10) // Align to left side of right section (near center)
            .attr('y', 0)
            .text(this.rightLabel)
            .style('fill', subTextColor)
            .style('font-size', '14px')
            .style('font-weight', '500')
            .style('text-anchor', 'start'); // Left align text

        // Separator Line
        svg.append('line')
            .attr('x1', centerAxis)
            .attr('y1', 0)
            .attr('x2', centerAxis)
            .attr('y2', height)
            .style('stroke', this.isDarkTheme ? '#2A2A2E' : '#EAEAEA')
            .style('stroke-width', 1);

        // Bars Groups
        const bars = svg.selectAll('.bar-group')
            .data(this.data)
            .enter()
            .append('g')
            .attr('class', 'bar-group')
            .attr('transform', d => `translate(0, ${y(d.label)})`);

        // Country Labels
        bars.append('text')
            .attr('x', 0)
            .attr('y', y.bandwidth() / 2)
            .attr('dy', '.35em')
            .text(d => d.label)
            .style('fill', textColor)
            .style('font-size', '14px')
            .style('font-weight', '500')
            .each(function (d) {
                // Truncate if too long
                const self = d3.select(this);
                let textLength = self.node()?.getComputedTextLength() || 0;
                let text = d.label;
                while (textLength > labelWidth - 10 && text.length > 0) {
                    text = text.slice(0, -1);
                    self.text(text + '...');
                    textLength = self.node()?.getComputedTextLength() || 0;
                }
            })
            .append('title')
            .text(d => d.label);

        // Left Bars (Impressions) - Growing Right to Left?
        // Wait, in Figma screenshot:
        // "India" -> [Blue Bar 85,697] | [Teal Bar 95,697]
        // The Blue bar seems to be aligned to the RIGHT of its column (next to separator).
        // The Teal bar is aligned to the LEFT of its column (next to separator).
        // Yes, divergent from center.

        // Left Rect (Blue)
        bars.append('rect')
            .attr('x', d => centerAxis - xLeft(Number(d.leftValue)))
            .attr('y', 0)
            .attr('width', d => Math.max(0, xLeft(Number(d.leftValue))))
            .attr('height', y.bandwidth())
            .attr('fill', this.leftColor)
            .attr('rx', 4) // Rounded corners
            .attr('ry', 4);

        // Left Value Text
        bars.append('text')
            .attr('y', y.bandwidth() / 2)
            .attr('dy', '.35em')
            .text(d => d.leftLabelFormatted || Number(d.leftValue).toLocaleString())
            .each(function (d) {
                const self = d3.select(this);
                const textWidth = self.node()?.getComputedTextLength() || 0;
                const barWidth = Math.max(0, xLeft(Number(d.leftValue)));
                
                if (barWidth > textWidth + 16) {
                    // Fits inside
                    self.attr('x', centerAxis - 10)
                        .attr('text-anchor', 'end')
                        .style('fill', '#FFFFFF');
                } else {
                    // Place outside to the left
                    self.attr('x', centerAxis - barWidth - 8)
                        .attr('text-anchor', 'end')
                        .style('fill', textColor);
                }
            })
            .style('font-size', '12px')
            .style('font-weight', '600');

        // Right Rect (Teal)
        bars.append('rect')
            .attr('x', centerAxis) // Start at center
            .attr('y', 0)
            .attr('width', d => Math.max(0, xRight(Number(d.rightValue))))
            .attr('height', y.bandwidth())
            .attr('fill', this.rightColor)
            .attr('rx', 4)
            .attr('ry', 4);

        // Right Value Text
        bars.append('text')
            .attr('y', y.bandwidth() / 2)
            .attr('dy', '.35em')
            .text(d => d.rightLabelFormatted || Number(d.rightValue).toLocaleString())
            .each(function (d) {
                const self = d3.select(this);
                const textWidth = self.node()?.getComputedTextLength() || 0;
                const barWidth = Math.max(0, xRight(Number(d.rightValue)));
                
                if (barWidth > textWidth + 16) {
                    // Fits inside
                    self.attr('x', centerAxis + 10)
                        .attr('text-anchor', 'start')
                        .style('fill', '#FFFFFF');
                } else {
                    // Place outside to the right
                    self.attr('x', centerAxis + barWidth + 8)
                        .attr('text-anchor', 'start')
                        .style('fill', textColor);
                }
            })
            .style('font-size', '12px')
            .style('font-weight', '600');
    }
}
